/**
 * Firebase-backed profile administration.
 *
 * A thin client over the Cloud Functions callables. Deliberately thin: every
 * rule that matters — ownership, plan limits, validation, all cryptography —
 * runs on the server (§5). Nothing here may be treated as a control, and nothing
 * here ever sees a phrase lookup, a PIN hash or an encryption key.
 *
 * The `addContact` / `updateContact` / `removeContact` methods exist to satisfy
 * the interface the Phase 1 components were written against. They are implemented
 * as read-modify-write over the single `updateRecoveryProfile` callable, so the
 * server keeps one code path for contact changes and re-encrypts the payload
 * once. The read-modify-write is not atomic across concurrent editors — see the
 * note on `writeContacts` below.
 */

import { getFunctions, httpsCallable } from "firebase/functions";
import { REGION } from "../../config/regions.js";

/**
 * Turn a callable rejection into a message safe to show a user.
 *
 * The server already returns plain-language, non-sensitive messages (§9), so
 * they pass through unchanged. Anything without a message is a transport
 * failure, which gets a generic line rather than a raw network error.
 *
 * @param {unknown} error
 * @returns {Error}
 */
function toUserError(error) {
  const message =
    error && typeof error === "object" && "message" in error && typeof error.message === "string"
      ? error.message
      : "";

  // The SDK prefixes callable errors with the status, e.g. "resource-exhausted".
  const cleaned = message.replace(/^[a-z-]+:\s*/i, "").trim();
  return new Error(
    cleaned.length > 0 ? cleaned : "We could not reach KinCode. Check your connection and retry.",
  );
}

/**
 * Map a server profile onto the shape components already expect.
 *
 * The server speaks the storage vocabulary (`status`, `firstName`); the UI type
 * predates it (`enabled`, `name`). Translating here keeps the components
 * untouched, which is the point of the interface.
 *
 * @param {any} raw
 * @returns {import("../../types/models.js").RecoveryProfile}
 */
function toProfile(raw) {
  return {
    id: raw.id,
    label: raw.label ?? "",
    enabled: raw.status !== "disabled",
    contacts: (raw.contacts ?? []).map(
      (/** @type {any} */ contact, /** @type {number} */ index) => ({
        // The server stores an ordered array with no per-contact ids — order is
        // the identity. A stable index-derived id keeps the list components happy
        // without inventing state the server does not hold.
        id: `${raw.id}:${index}`,
        name: contact.firstName ?? "",
        telephone: contact.telephone ?? "",
        ...(contact.relationship ? { relationship: contact.relationship } : {}),
      }),
    ),
    phrase: raw.phrase,
    pin: raw.pin,
    ...(typeof raw.maxContacts === "number" ? { maxContacts: raw.maxContacts } : {}),
  };
}

/**
 * Map UI contacts back to the server's shape.
 *
 * @param {import("../../types/models.js").Contact[]} contacts
 */
function toServerContacts(contacts) {
  return contacts.map((contact) => ({
    firstName: contact.name,
    telephone: contact.telephone,
    ...(contact.relationship ? { relationship: contact.relationship } : {}),
  }));
}

/**
 * @param {import("./app.js").FirebaseContext} context
 * @returns {import("../interfaces.js").ProfileService}
 */
export function createFirebaseProfileService(context) {
  const functions = getFunctions(context.app, REGION);

  /**
   * @template T
   * @param {string} name
   * @returns {(payload?: unknown) => Promise<T>}
   */
  function callable(name) {
    const fn = httpsCallable(functions, name);
    return async (payload = {}) => {
      try {
        const result = await fn(payload);
        return /** @type {T} */ (result.data);
      } catch (error) {
        throw toUserError(error);
      }
    };
  }

  const callCreate = callable("createRecoveryProfile");
  const callList = callable("listRecoveryProfiles");
  const callGet = callable("getRecoveryProfileForOwner");
  const callUpdate = callable("updateRecoveryProfile");
  const callStatus = callable("setRecoveryProfileStatus");
  const callDelete = callable("deleteRecoveryProfile");
  const callRegenerate = callable("regenerateProfileCredentials");

  /**
   * Replace a profile's contact list.
   *
   * Reads the current profile, applies the change, and writes the whole array
   * back. Two editors working on the same profile at once can therefore lose one
   * another's change — last write wins. That is acceptable for a single-adult
   * account and becomes a real concern only when §12's "additional adult
   * administrators" arrive, which is when this should move to a server-side
   * transaction rather than a client read-modify-write.
   *
   * @param {string} profileId
   * @param {(contacts: import("../../types/models.js").Contact[]) => import("../../types/models.js").Contact[]} change
   */
  async function writeContacts(profileId, change) {
    const current = toProfile(await callGet({ profileId }));
    const next = change(current.contacts);
    await callUpdate({ profileId, contacts: toServerContacts(next) });
    return next;
  }

  return {
    async list() {
      const data = /** @type {{ profiles: unknown[] }} */ (await callList());
      return data.profiles.map(toProfile);
    },

    async get(id) {
      try {
        return toProfile(await callGet({ profileId: id }));
      } catch {
        // The interface says null for "not found or not owned", and the server
        // gives the same error for both — deliberately.
        return null;
      }
    },

    async create(label) {
      return toProfile(await callCreate({ label, contacts: [] }));
    },

    async rename(profileId, label) {
      // Send only the label. The server merges against the current payload
      // (label ?? current.label, contacts ?? current.contacts), so the contacts
      // and the display phrase are carried across untouched.
      await callUpdate({ profileId, label });
    },

    async addContact(profileId, contact) {
      const current = toProfile(await callGet({ profileId }));
      /** @type {import("../../types/models.js").Contact} */
      const created = { ...contact, id: `${profileId}:${current.contacts.length}` };
      await callUpdate({
        profileId,
        contacts: toServerContacts([...current.contacts, created]),
      });
      return created;
    },

    async updateContact(profileId, contact) {
      await writeContacts(profileId, (contacts) =>
        contacts.map((candidate) => (candidate.id === contact.id ? { ...contact } : candidate)),
      );
    },

    async removeContact(profileId, contactId) {
      await writeContacts(profileId, (contacts) =>
        contacts.filter((candidate) => candidate.id !== contactId),
      );
    },

    async setEnabled(profileId, enabled) {
      await callStatus({ profileId, status: enabled ? "active" : "disabled" });
    },

    async remove(profileId) {
      await callDelete({ profileId });
    },

    async regenerateCredentials(profileId, options) {
      /** @type {Record<string, unknown>} */
      const payload = {
        profileId,
        regeneratePhrase: options?.phrase ?? false,
        regeneratePin: options?.pin ?? true,
      };
      // Only sent when actually chosen — an absent key means "generate one",
      // which the callable's schema treats differently from an empty string.
      if (options?.customPin) {
        payload.pin = options.customPin;
      }
      const data = /** @type {{ phrase?: string, pin?: string }} */ (await callRegenerate(payload));
      // Built conditionally rather than by spreading `undefined` values: the
      // absence of a key means "not reissued", which a present-but-undefined key
      // would not distinguish.
      /** @type {{ phrase?: string, pin?: string }} */
      const issued = {};
      if (typeof data.phrase === "string") {
        issued.phrase = data.phrase;
      }
      if (typeof data.pin === "string") {
        issued.pin = data.pin;
      }
      return issued;
    },
  };
}
